import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { Segment, SegmentCategory } from '@interfaces/segments/domain';
import { segmentsAdapter, segmentCategoriesAdapter } from './segmentsSlice';
import { selectSelectedMarketIds } from '@/store/slices/userSelection/selectors';
import { isAvailableInSelectedMarkets } from '@utils/markets/availability';

const segsSelectors = segmentsAdapter.getSelectors(
  (s: RootState) => s.activeGroup.segments.segments,
);
const catsSelectors = segmentCategoriesAdapter.getSelectors(
  (s: RootState) => s.activeGroup.segments.categories,
);

export const selectAllSegments = segsSelectors.selectAll;
export const selectAllSegmentCategories = catsSelectors.selectAll;
export const selectSegmentByCode = segsSelectors.selectById;
export const selectSegmentCategoryByCode = catsSelectors.selectById;

export const selectSegmentsForMarkets = createSelector(
  [selectAllSegments, selectSelectedMarketIds],
  (segments, markets) =>
    segments.filter(
      // SYSTEM segments arrive with `markets: []` meaning "all markets" —
      // the deny-on-empty rule must never reach them. Only USER segments
      // carry real applicability. Do not "fix" this into the predicate.
      (s) =>
        s.type === 'SYSTEM' ||
        isAvailableInSelectedMarkets(s.markets, markets),
    ),
);

export const selectAppliedSegmentCodes = createSelector(
  [selectAllSegments],
  (segments) => segments.filter((s) => s.isApplied).map((s) => s.code),
);

export interface SegmentPickerSection {
  category: SegmentCategory;
  segments: Segment[];
}

export const selectSegmentPickerData = createSelector(
  [selectAllSegmentCategories, selectSegmentsForMarkets],
  (categories, segments): SegmentPickerSection[] => {
    const byCategory = new Map<string, Segment[]>();
    for (const s of segments) {
      const arr = byCategory.get(s.categoryCode) ?? [];
      arr.push(s);
      byCategory.set(s.categoryCode, arr);
    }
    return categories
      .map((c) => ({ category: c, segments: byCategory.get(c.code) ?? [] }))
      .filter((sec) => sec.segments.length > 0);
  },
);
